/**
 * Custom Product Media Gallery (Swiper 11) for Dawn 15.x
 *
 * - One <custom-product-gallery> element per product section, scoped by #product-gallery-{section.id}
 * - Main Swiper + Thumbnail Swiper connected with the Swiper `thumbs` API
 * - Independent desktop / mobile behaviour (breakpoint comes from the section setting)
 * - Variant media sync re-uses Dawn's own PUB_SUB_EVENTS.variantChange event published by product-info.js
 * - Safe for Theme Editor re-renders, Section Rendering API, quick-add modal and dynamic DOM
 *   (custom element lifecycle + data-initialized guard + destroy on disconnect)
 */
(() => {
  if (customElements.get('custom-product-gallery')) return;

  // Swiper classes are renamed so this gallery never picks up (or leaks) global `.swiper*` styles.
  const SWIPER_CLASSES = {
    containerModifierClass: 'cpg-swiper-',
    slideActiveClass: 'is-slide-active',
    slideVisibleClass: 'is-slide-visible',
    slideFullyVisibleClass: 'is-slide-fully-visible',
    slideNextClass: 'is-slide-next',
    slidePrevClass: 'is-slide-prev',
    slideBlankClass: 'is-slide-blank',
    lazyPreloaderClass: 'custom-product-gallery__preloader',
  };

  let swiperLoader = null;
  const loadSwiper = (src) => {
    if (typeof window.Swiper === 'function') return Promise.resolve(window.Swiper);
    if (swiperLoader) return swiperLoader;

    swiperLoader = new Promise((resolve, reject) => {
      // Re-use an existing <script> for swiper.js (theme.liquid already loads it) instead of adding a duplicate.
      const existing = Array.from(document.scripts).find((s) => s.src && /\/swiper(\.min)?\.js/.test(s.src));
      const onReady = () => (typeof window.Swiper === 'function' ? resolve(window.Swiper) : reject(new Error('Swiper not available')));

      if (existing) {
        existing.addEventListener('load', onReady, { once: true });
        existing.addEventListener('error', reject, { once: true });
        // Script may already have executed between checks.
        window.setTimeout(() => typeof window.Swiper === 'function' && onReady(), 0);
        return;
      }
      if (!src) return reject(new Error('Swiper source missing'));

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = onReady;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return swiperLoader;
  };

  class CustomProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleVariantInputChange = this.handleVariantInputChange.bind(this);
      this.handleClick = this.handleClick.bind(this);

      this.readConfig();
      this.productInfo = this.closest('product-info');

      this.mql = window.matchMedia(`(min-width: ${this.config.desktopBreakpoint || 750}px)`);
      this.mql.addEventListener('change', this.handleBreakpointChange);
      this.addEventListener('click', this.handleClick);
      this.bindVariantSync();

      loadSwiper(this.dataset.swiperSrc)
        .then(() => {
          if (!this.isConnected || this.dataset.initialized !== 'true') return;
          this.init(this.config.initialIndex || 0);
        })
        .catch((error) => console.error('[custom-product-gallery]', error));
    }

    disconnectedCallback() {
      this.destroy();
      this.mql?.removeEventListener('change', this.handleBreakpointChange);
      this.removeEventListener('click', this.handleClick);
      this.unsubscribeVariant?.();
      this.unsubscribeVariant = null;
      this.productInfo?.removeEventListener('change', this.handleVariantInputChange);
      this.dataset.initialized = 'false';
    }

    /* ------------------------------------------------------------------ */
    /* Setup                                                              */
    /* ------------------------------------------------------------------ */

    readConfig() {
      try {
        this.config = JSON.parse(this.querySelector('[data-cpg-config]')?.textContent || '{}');
      } catch (e) {
        this.config = {};
      }
      this.config.desktop = this.config.desktop || {};
      this.config.mobile = this.config.mobile || {};
      this.config.i18n = this.config.i18n || {};
    }

    get elements() {
      return {
        main: this.querySelector('[data-cpg-main]'),
        thumbs: this.querySelector('[data-cpg-thumbs]'),
        prev: this.querySelector('[data-cpg-prev]'),
        next: this.querySelector('[data-cpg-next]'),
        thumbPrev: this.querySelector('[data-cpg-thumb-prev]'),
        thumbNext: this.querySelector('[data-cpg-thumb-next]'),
        current: this.querySelector('[data-cpg-current]'),
        total: this.querySelector('[data-cpg-total]'),
      };
    }

    get slides() {
      return Array.from(this.querySelectorAll('[data-cpg-slide]'));
    }

    get isDesktop() {
      return this.mql.matches;
    }

    init(startIndex = 0) {
      if (typeof window.Swiper !== 'function') return;
      const els = this.elements;
      if (!els.main) return;

      const slideCount = this.slides.length;
      const mode = this.isDesktop ? this.config.desktop : this.config.mobile;
      const index = Math.min(Math.max(startIndex, 0), Math.max(slideCount - 1, 0));
      this.dataset.mode = this.isDesktop ? 'desktop' : 'mobile';

      // Reveal all slides *before* Swiper measures them.
      this.classList.add('is-ready');

      if (slideCount < 2) {
        this.updateUi(0);
        return;
      }

      // Thumbnail Swiper (only when thumbnails are visible for the current breakpoint)
      if (mode.thumbs && els.thumbs) {
        this.thumbsSwiper = new window.Swiper(els.thumbs, {
          ...SWIPER_CLASSES,
          wrapperClass: 'custom-product-gallery__wrapper',
          slideClass: 'custom-product-gallery__thumb-slide',
          direction: mode.direction === 'vertical' ? 'vertical' : 'horizontal',
          slidesPerView: 'auto',
          spaceBetween: Number(mode.thumbGap) || 0,
          watchSlidesProgress: true,
          watchOverflow: true,
          slideToClickedSlide: false,
          simulateTouch: true,
          threshold: 5,
          a11y: { enabled: false },
          keyboard: { enabled: false },
          navigation: {
            prevEl: els.thumbPrev,
            nextEl: els.thumbNext,
            disabledClass: 'is-disabled',
            lockClass: 'is-locked',
          },
        });
      }

      const cfg = this.config;
      const allowMouseDrag = this.isDesktop ? cfg.desktopDrag !== false : true;

      this.mainSwiper = new window.Swiper(els.main, {
        ...SWIPER_CLASSES,
        wrapperClass: 'custom-product-gallery__wrapper',
        slideClass: 'custom-product-gallery__slide',
        initialSlide: index,
        slidesPerView: 1,
        spaceBetween: Number(cfg.slideGap) || 0,
        speed: Number(cfg.speed) || 400,
        rewind: !!cfg.rewind,
        effect: cfg.effect === 'fade' ? 'fade' : 'slide',
        fadeEffect: { crossFade: true },
        simulateTouch: allowMouseDrag,
        grabCursor: allowMouseDrag,
        threshold: 6,
        watchOverflow: true,
        // Let customers interact with 3D models and native video controls without changing slide.
        noSwipingSelector: 'model-viewer, .shopify-model-viewer-ui, video, iframe',
        keyboard: { enabled: true, onlyInViewport: true },
        a11y: {
          enabled: true,
          prevSlideMessage: cfg.i18n.prev,
          nextSlideMessage: cfg.i18n.next,
          firstSlideMessage: cfg.i18n.first,
          lastSlideMessage: cfg.i18n.last,
          slideLabelMessage: `${cfg.i18n.slide ? cfg.i18n.slide + ' ' : ''}{{index}} / {{slidesLength}}`,
          containerRoleDescriptionMessage: 'carousel',
          itemRoleDescriptionMessage: 'slide',
          notificationClass: 'custom-product-gallery__notification',
          containerMessage: cfg.i18n.carousel,
        },
        navigation: {
          prevEl: els.prev,
          nextEl: els.next,
          disabledClass: 'is-disabled',
          lockClass: 'is-locked',
        },
        thumbs: {
          swiper: this.thumbsSwiper || null,
          slideThumbActiveClass: 'is-thumb-active',
          thumbsContainerClass: 'custom-product-gallery__thumbs--linked',
          autoScrollOffset: 1,
        },
        on: {
          afterInit: (swiper) => this.updateUi(swiper.activeIndex),
          slideChange: (swiper) => this.onSlideChange(swiper),
        },
      });
    }

    destroy() {
      if (this.mainSwiper && !this.mainSwiper.destroyed) this.mainSwiper.destroy(true, true);
      if (this.thumbsSwiper && !this.thumbsSwiper.destroyed) this.thumbsSwiper.destroy(true, true);
      this.mainSwiper = null;
      this.thumbsSwiper = null;
    }

    reinit(index) {
      const current = typeof index === 'number' ? index : this.mainSwiper?.activeIndex ?? this.config.initialIndex ?? 0;
      this.destroy();
      this.init(current);
    }

    handleBreakpointChange() {
      this.reinit();
    }

    /* ------------------------------------------------------------------ */
    /* Slide change / UI                                                  */
    /* ------------------------------------------------------------------ */

    onSlideChange(swiper) {
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      this.updateUi(swiper.activeIndex);
      const slide = this.slides[swiper.activeIndex];
      this.dispatchEvent(
        new CustomEvent('custom-product-gallery:slide-change', {
          bubbles: true,
          detail: { index: swiper.activeIndex, mediaId: slide?.dataset.mediaId, slide },
        })
      );
    }

    updateUi(index) {
      const slides = this.slides;
      const count = slides.length || 1;
      const els = this.elements;

      this.style.setProperty('--cpg-index', index);
      this.style.setProperty('--cpg-count', count);
      this.style.setProperty('--cpg-progress', (index + 1) / count);
      if (els.current) els.current.textContent = index + 1;
      if (els.total) els.total.textContent = count;

      this.querySelectorAll('[data-cpg-dot]').forEach((dot) => {
        const active = Number(dot.dataset.index) === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.querySelectorAll('[data-cpg-thumb]').forEach((thumb) => {
        if (Number(thumb.dataset.index) === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      // If thumbnails are visible but not initialised as Swiper (edge cases), keep active thumb in view.
      if (this.thumbsSwiper && !this.thumbsSwiper.destroyed) {
        const visible = this.thumbsSwiper.visibleSlidesIndexes || [];
        if (visible.length && !visible.includes(index)) this.thumbsSwiper.slideTo(index);
      }

      this.preloadAround(index);
    }

    // Native lazy-loading does not see off-screen slides inside an overflow:hidden slider,
    // so eagerly load the neighbours of the active slide.
    preloadAround(index) {
      const slides = this.slides;
      [index - 1, index, index + 1].forEach((i) => {
        const slide = slides[i];
        if (!slide) return;
        slide.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
      });
    }

    handleClick(event) {
      const dot = event.target.closest('[data-cpg-dot]');
      const thumb = event.target.closest('[data-cpg-thumb]');
      const target = dot || thumb;
      if (!target || !this.contains(target)) return;
      const index = Number(target.dataset.index);
      if (Number.isNaN(index)) return;

      if (this.mainSwiper && !this.mainSwiper.destroyed) {
        this.mainSwiper.slideTo(index);
      }
    }

    /* ------------------------------------------------------------------ */
    /* Public API                                                         */
    /* ------------------------------------------------------------------ */

    /**
     * Move the main slider to the slide that contains the given Shopify media id.
     * @param {string|number} mediaId Shopify media id (e.g. variant.featured_media.id)
     * @param {{ scroll?: boolean, speed?: number }} options
     * @returns {boolean} true when the media exists in this gallery
     */
    goToMedia(mediaId, options = {}) {
      if (mediaId == null) return false;
      const id = String(mediaId);
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === id);
      if (index < 0) return false;

      if (this.mainSwiper && !this.mainSwiper.destroyed) {
        if (this.mainSwiper.activeIndex !== index) this.mainSwiper.slideTo(index, options.speed);
      } else {
        this.config.initialIndex = index;
      }

      if (options.scroll) this.scrollIntoViewIfNeeded();
      return true;
    }

    // Kept for compatibility with Dawn's product-info.js API name.
    setActiveMedia(sectionMediaId) {
      const id = String(sectionMediaId).split('-').pop();
      this.goToMedia(id, { scroll: true });
    }

    scrollIntoViewIfNeeded() {
      const stickyHeader = document.querySelector('sticky-header');
      if (stickyHeader) stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));

      window.setTimeout(() => {
        const rect = this.getBoundingClientRect();
        // Same behaviour as Dawn: only scroll when the gallery top is above the viewport.
        if (rect.top > -0.5) return;
        window.scrollTo({ top: rect.top + window.scrollY, behavior: 'smooth' });
      });
    }

    /* ------------------------------------------------------------------ */
    /* Variant media sync (re-uses Dawn's product-info.js flow)           */
    /* ------------------------------------------------------------------ */

    get productSectionId() {
      if (this.productInfo) return this.productInfo.dataset.originalSection || this.productInfo.dataset.section;
      return this.config.sectionId || this.dataset.sectionId;
    }

    bindVariantSync() {
      // 1) Dawn publishes this after it has fetched and applied the new variant's section HTML.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }
      // 2) Fallback for third-party variant pickers: Dawn (and most apps) dispatch `change` on the form's input[name="id"].
      this.productInfo?.addEventListener('change', this.handleVariantInputChange);
    }

    handleVariantChange(event) {
      const data = event?.data;
      if (!data || data.sectionId !== this.productSectionId) return;

      if (this.config.hideVariants && data.html) this.syncFromHtml(data.html, data.sectionId);

      const mediaId = data.variant?.featured_media?.id;
      if (mediaId) this.goToMedia(mediaId, { scroll: true });
    }

    handleVariantInputChange(event) {
      const input = event.target;
      if (!input || input.name !== 'id' || !input.closest('form[action*="/cart/add"]')) return;
      const variantId = String(input.value || '');
      if (!variantId) return;

      const map = this.variantMediaMap;
      const mediaId = map[variantId];
      if (mediaId) this.goToMedia(mediaId);
    }

    get variantMediaMap() {
      if (this._variantMediaMap) return this._variantMediaMap;
      const map = {};
      const selectedJson = this.querySelector('script[data-cpg-variant-media]');
      if (selectedJson) {
        try {
          Object.assign(map, JSON.parse(selectedJson.textContent));
        } catch (e) {
          /* ignore */
        }
      }
      this._variantMediaMap = map;
      return map;
    }

    /**
     * "Hide other variants' media" is enabled: the set of slides differs per variant.
     * Take the gallery markup Dawn already fetched for the new variant and rebuild Swiper.
     */
    syncFromHtml(html, sourceSectionId) {
      const source = html.getElementById(`product-gallery-${sourceSectionId}`);
      if (!source) return;

      const newIds = Array.from(source.querySelectorAll('[data-cpg-slide]'))
        .map((slide) => slide.dataset.mediaId)
        .join(',');
      const currentIds = this.slides.map((slide) => slide.dataset.mediaId).join(',');
      if (newIds === currentIds) return;

      let markup = source.innerHTML;
      const currentSectionId = this.dataset.sectionId;
      if (currentSectionId && currentSectionId !== sourceSectionId) {
        markup = markup.split(sourceSectionId).join(currentSectionId); // quick-add modal uses prefixed ids
      }

      this.destroy();
      this.classList.remove('is-ready');
      this.innerHTML = markup;
      const inlineStyle = source.getAttribute('style');
      if (inlineStyle) this.setAttribute('style', inlineStyle);
      this.readConfig();
      this._variantMediaMap = null;
      this.init(this.config.initialIndex || 0);

      if (typeof window.enableZoomOnHover === 'function' && this.productInfo?.hasAttribute('data-zoom-on-hover')) {
        window.enableZoomOnHover(2);
      }
    }
  }

  customElements.define('custom-product-gallery', CustomProductGallery);
})();
